import { FC } from "react";
import Logo from "designs/DesignB/components/Logo/Logo";
import Navigation from "shared/Navigation/Navigation";
import MenuBar from "shared/MenuBar/MenuBar";
import { Link, useLocation } from "react-router-dom";
import { useData } from "data/data-provider";

export interface MainNav1Props {
  className?: string;
}

const DARK_HEADER_PATHS = [
  "/vehicles",
  // 1NOW-10372: was "/rental-terms", which no longer exists — the terms page
  // moved to /terms-and-conditions and privacy policy joined it.
  "/terms-and-conditions",
  "/privacy-policy",
  "/car-listing-detail",
  "/booking-details",
  "/checkout",
];

// Header layout variants, chosen in the FMS website builder and stored on
// CompanyWebsiteDetail.header_layout:
//   "classic"      → logo left, nav right, "Book Now" CTA pinned right; default
//   "split_cta"    → logo left, nav centered, "Book Now" CTA pinned right
//   "drawer_right" → logo left, "Book Now" CTA + menu button right; nav links
//                    live in a slide-out panel from the right (no inline nav)
//
// The "Book Now" CTA is the primary conversion action and is shown in every
// layout (1NOW-1240) — only its placement and the nav arrangement differ.
type HeaderVariant = "classic" | "split_cta" | "drawer_right";

/**
 * Design B desktop / mobile nav.
 *
 * On the home banner (transparent path) the bar floats over the hero with a
 * frosted glass; on inner pages it gets a solid surface so text reads against
 * white sections. The internal arrangement (logo / nav / CTA positions)
 * follows the chosen header layout variant; the mobile hamburger is shared
 * across every variant.
 */
const MainNav: FC<MainNav1Props> = ({ className = "" }) => {
  // `!` on the show/hide variants below: see shared/Navigation/Navigation.tsx
  // (Meta Event Setup Tool injects a competing Tailwind sheet).
  const location = useLocation();
  const { companyDetails } = useData();
  const isInnerPage = DARK_HEADER_PATHS.includes(location.pathname);

  // Source of truth for the layout is the FMS-set `header_layout`. A
  // `?headerVariant=` query param overrides it for live preview (handy
  // before the tenant has saved a choice). Defaults to "classic" so every
  // existing tenant keeps the original logo-left / nav-right bar.
  const params = new URLSearchParams(location.search);
  const variant: HeaderVariant =
    (params.get("headerVariant") as HeaderVariant) ||
    (companyDetails?.header_layout as HeaderVariant) ||
    "classic";

  return (
    <div className={`nc-MainNav1 relative z-10 ${className}`}>
      <div
        className={`nc-header-bg backdrop-blur-md transition-colors duration-300 ${
          isInnerPage
            ? "bg-bg-color/95 border-b border-text-color/10"
            : "bg-bg-color/35"
        }`}
      >
        {variant === "drawer_right" ? (
          /* ───────────────────────────────────────────────────────────
             Right nav panel — logo left · Book Now CTA + menu button right.
             The menu button opens the existing right-side slide-out panel
             (NavMobile) at ALL breakpoints, so the nav lives entirely in
             the drawer instead of inline; the Book Now CTA stays in the bar
             (from sm up) so the primary conversion action is reachable
             without opening the drawer. Minimal, gallery-style header.
             ─────────────────────────────────────────────────────────── */
          <div className="container py-3 relative flex items-center justify-between gap-4">
            {/* Brand */}
            <div className="flex items-center">
              <Logo />
            </div>

            {/* Right cluster: persistent Book Now CTA + menu trigger */}
            <div className="ml-auto flex items-center gap-3">
              <Link
                to="/vehicles"
                className="hidden sm:!inline-flex items-center rounded-full bg-button-color text-white px-5 py-2.5 text-sm font-semibold shadow-sm transition-all duration-300 hover:bg-primary-200 hover:text-on-primary hover:shadow-lg hover:shadow-primary-200/30 active:scale-95"
              >
                Book Now
              </Link>
              <div className="flex flex-shrink-0 items-center justify-end text-text-color">
                <MenuBar />
              </div>
            </div>
          </div>
        ) : variant === "split_cta" ? (
          /* ───────────────────────────────────────────────────────────
             Split + CTA — logo left · nav dead-center · Book Now right.
             The nav is absolutely centered so it stays centered on the bar
             regardless of how wide the logo or CTA get. Below lg the nav
             collapses into the hamburger (same as classic) and the CTA
             stays visible from sm up.
             ─────────────────────────────────────────────────────────── */
          <div className="container py-3 relative flex items-center gap-4">
            {/* Brand */}
            <div className="flex flex-shrink-0 items-center">
              <Logo />
            </div>

            {/* Centered nav */}
            <nav className="hidden lg:!flex absolute left-1/2 -translate-x-1/2 items-center">
              <Navigation />
            </nav>

            {/* Right cluster: CTA (desktop/tablet) + mobile menu */}
            <div className="ml-auto flex items-center justify-end gap-3">
              <Link
                to="/vehicles"
                // Match the established Design B primary-CTA treatment
                // (FleetCard / HomeAboutUs): a dark `button-color` fill with
                // white text reads cleanly on every variant palette — unlike a
                // raw primary fill, which fails on light primaries (gold/amber).
                // Hover flips to the primary accent, consistent with the rest
                // of the site's CTAs.
                className="hidden sm:!inline-flex items-center rounded-full bg-button-color text-white px-5 py-2.5 text-sm font-semibold shadow-sm transition-all duration-300 hover:bg-primary-200 hover:text-on-primary hover:shadow-lg hover:shadow-primary-200/30 active:scale-95"
              >
                Book Now
              </Link>
              <div className="flex flex-shrink-0 items-center text-text-color lg:!hidden">
                <MenuBar />
              </div>
            </div>
          </div>
        ) : (
          /* ───────────────────────────────────────────────────────────
             Classic (default) — logo left · nav + Book Now CTA right.
             Use plain `container` so the header's logo + nav share the same
             horizontal gutter as the banner heading and every home-page
             section below. The desktop nav, the persistent Book Now CTA
             (from sm up) and the mobile menu trigger share one right-side
             cluster so the CTA sits beside the nav on desktop and beside
             the hamburger on mobile (1NOW-1240).
             ─────────────────────────────────────────────────────────── */
          <div className="container py-3 relative flex items-center justify-between gap-4">
            {/* Brand */}
            <div className="flex items-center">
              <Logo />
            </div>

            {/* Right cluster: desktop nav + Book Now CTA + mobile menu */}
            <div className="ml-auto flex items-center gap-4">
              <nav className="hidden lg:!flex items-center">
                <Navigation />
              </nav>

              <Link
                to="/vehicles"
                className="hidden sm:!inline-flex items-center rounded-full bg-button-color text-white px-5 py-2.5 text-sm font-semibold shadow-sm transition-all duration-300 hover:bg-primary-200 hover:text-on-primary hover:shadow-lg hover:shadow-primary-200/30 active:scale-95"
              >
                Book Now
              </Link>

              <div className="flex flex-shrink-0 items-center justify-end text-text-color lg:!hidden">
                <MenuBar />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MainNav;
